import { Clock } from 'lucide-react';
import type {
    DwellKpis,
    DwellTrendPoint,
    DockPerformanceRow,
    CarrierLeaderboardRow,
} from '../../types/analyticsDwell';

export interface DwellExportProps {
    /** KPI values forwarded directly from the page — no re-fetch */
    kpis: DwellKpis;
    trend: DwellTrendPoint[];
    dockPerformance: DockPerformanceRow[];
    carrierLeaderboard: CarrierLeaderboardRow[];
    /** Context passed from parent */
    graceMinutes: number;
    hourlyRate: number;
    loadsWithDwell: number;
    computedAt: number;
    rangeLabel: string;      // 'Last 7 Days' | 'Last 14 Days' | 'Last 30 Days'
    startDate: Date;
    endDate: Date;
    facilityName: string;
    onClose: () => void;
}

function formatDate(d: Date): string {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function DwellAnalyticsExport({
    kpis,
    trend,
    dockPerformance,
    carrierLeaderboard,
    graceMinutes,
    hourlyRate,
    loadsWithDwell,
    computedAt,
    rangeLabel,
    startDate,
    endDate,
    facilityName,
    onClose,
}: DwellExportProps) {

    const handlePrint = () => window.print();

    return (
        /* Same CSS isolation class as EnvironmentalImpactExport & MasterBOLModal */
        <div className="fixed inset-0 z-[400] flex items-center justify-center bol-print-wrapper">

            {/* Backdrop — visible on screen, hidden when printing */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm print:hidden"
                onClick={onClose}
            />

            {/* Document card */}
            <div className="relative w-full max-w-2xl max-h-[92vh] print:mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col print:max-h-none print:rounded-none print:shadow-none print:overflow-visible print:max-w-none">

                {/* Toolbar — screen only */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-slate-50 print:hidden">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        <Clock size={15} className="text-cyan-500" />
                        Dwell &amp; Detention Report
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            title="For clean PDF export: disable 'Headers and footers' in your browser print settings."
                            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white transition-colors"
                        >
                            Print / Save PDF
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto print:overflow-visible">
                    <div className="bol-print p-10 space-y-7 text-slate-900 print:text-black print:bg-white print:p-0 print:space-y-6">

                        {/* ── Section 0: Document title ── */}
                        <div className="text-center border-b-2 border-slate-900 pb-5 print:border-black print-avoid-break">
                            <div className="flex items-center justify-center gap-2 mb-1">
                                <Clock size={18} className="text-cyan-600 print:text-black" />
                                <h1 className="text-xl font-bold tracking-tight uppercase text-slate-900 print:text-black">
                                    Dwell &amp; Detention Analysis Report
                                </h1>
                            </div>
                            <p className="text-sm text-slate-600 print:text-slate-700 font-medium mt-1">
                                Dock Turnaround Performance and Detention Cost Summary
                            </p>
                        </div>

                        {/* ── Section 1: Facility identification ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Facility Details</SectionLabel>
                            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 border border-slate-300 print:border-black rounded-none p-4">
                                <InfoField label="Facility" value={facilityName} />
                                <InfoField
                                    label="Reporting Period"
                                    value={`${formatDate(startDate)} – ${formatDate(endDate)}`}
                                />
                                <InfoField label="Range" value={rangeLabel} />
                                <InfoField label="Generated" value={formatDate(new Date())} />
                            </div>
                        </div>

                        {/* ── Section 2: KPI summary ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Key Performance Indicators</SectionLabel>
                            <table className="w-full mt-3 text-sm border-collapse">
                                <thead>
                                    <tr className="bg-slate-100 print:bg-slate-100">
                                        <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Metric</th>
                                        <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Value</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                    <KpiRow label="Average Dwell Time" value={`${kpis.avgDwellMin} min`} />
                                    <KpiRow label="Average Wait Time" value={`${kpis.avgWaitMin} min`} />
                                    <KpiRow label="Detention Rate" value={`${kpis.detentionRatePct}%`} />
                                    <KpiRow label="Estimated Detention Cost" value={`$${kpis.estDetentionCost.toLocaleString()}`} bold />
                                    <KpiRow label="Loads Analyzed" value={`${loadsWithDwell}`} />
                                    <KpiRow label="Detention Free Time (Grace)" value={`${graceMinutes} min`} />
                                    <KpiRow label="Hourly Detention Rate" value={`$${hourlyRate}/hr`} />
                                </tbody>
                            </table>
                        </div>

                        {/* ── Section 3: Carrier detention leaderboard ── */}
                        {carrierLeaderboard.length > 0 && (
                            <div className="print-avoid-break">
                                <SectionLabel>Carrier Detention Leaderboard</SectionLabel>
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Carrier</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Loads</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Avg Dwell</th>
                                            <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Violations</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {carrierLeaderboard.map((c) => (
                                            <tr key={c.carrierKey}>
                                                <td className="px-4 py-2 text-slate-800 print:text-black border border-slate-200 print:border-black font-medium">{c.carrierName}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.totalLoads}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{c.avgDwellMin}m</td>
                                                <td className="px-4 py-2 text-right border border-slate-200 print:border-black font-mono font-bold" style={{ color: c.detentionViolations > 0 ? '#dc2626' : '#64748b' }}>
                                                    {c.detentionViolations}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Section 4: Dock performance ── */}
                        {dockPerformance.length > 0 && (
                            <div className="print-avoid-break">
                                <SectionLabel>Dock Performance Summary</SectionLabel>
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Dock</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Loads</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Avg Dwell</th>
                                            <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Over Grace %</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {dockPerformance.map((d) => (
                                            <tr key={d.dockId}>
                                                <td className="px-4 py-2 text-slate-800 print:text-black border border-slate-200 print:border-black font-medium">{d.dockLabel}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{d.loadCount}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{d.avgDwellMin}m</td>
                                                <td className="px-4 py-2 text-right border border-slate-200 print:border-black font-mono font-bold" style={{ color: d.overGracePct > 25 ? '#dc2626' : d.overGracePct > 10 ? '#d97706' : '#64748b' }}>
                                                    {d.overGracePct}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Section 5: Daily trend summary ── */}
                        {trend.length > 0 && trend.some(t => t.loadCount > 0) && (
                            <div className="print-avoid-break">
                                <SectionLabel>Daily Dwell Trend</SectionLabel>
                                <table className="w-full mt-3 text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-slate-100 print:bg-slate-100">
                                            <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Date</th>
                                            <th className="text-center text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Loads</th>
                                            <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 print:text-slate-600 px-4 py-2 border border-slate-300 print:border-black">Avg Dwell</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 print:divide-slate-300">
                                        {trend.filter(t => t.loadCount > 0).map((t) => (
                                            <tr key={t.date}>
                                                <td className="px-4 py-2 text-slate-700 print:text-black border border-slate-200 print:border-black">{t.date}</td>
                                                <td className="px-4 py-2 text-center text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{t.loadCount}</td>
                                                <td className="px-4 py-2 text-right text-slate-600 print:text-slate-800 border border-slate-200 print:border-black font-mono">{t.avgDwellMin}m</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* ── Section 6: Methodology ── */}
                        <div className="print-avoid-break">
                            <SectionLabel>Methodology</SectionLabel>
                            <p className="mt-3 text-sm text-slate-700 print:text-slate-800 leading-relaxed border border-slate-300 print:border-black p-4">
                                Dwell time is calculated from check-in to check-out for each completed appointment. Detention rate reflects the percentage of loads exceeding the configured grace period ({graceMinutes} minutes). Estimated detention cost applies the configured hourly rate (${hourlyRate}/hr) to minutes beyond grace. Outliers exceeding 24 hours are excluded. Data sourced from BayWhiz appointment lifecycle records.
                            </p>
                        </div>

                        {/* ── Footer ── */}
                        <div className="mt-8 pt-4 border-t-2 border-slate-300 print:border-black text-center print-avoid-break">
                            <p className="text-[11px] text-slate-400 print:text-slate-500">
                                Generated by <strong>BayWhiz Dock Scheduling Platform</strong>
                            </p>
                            <p className="text-[11px] text-slate-400 print:text-slate-500">
                                Dwell &amp; Detention analysis derived from appointment lifecycle timestamps
                            </p>
                            <p className="text-[11px] text-slate-400 print:text-slate-500 mt-1">
                                Report computed at {new Date(computedAt).toLocaleString()}
                            </p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

/* ── Sub-components (same pattern as EnvironmentalImpactExport) ── */
function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 print:text-slate-600 border-b border-slate-200 print:border-slate-400 pb-1">
            {children}
        </h2>
    );
}

function InfoField({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 print:text-slate-500 mb-0.5">{label}</div>
            <div className="text-sm font-semibold text-slate-900 print:text-black">{value}</div>
        </div>
    );
}

function KpiRow({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
    return (
        <tr>
            <td className={`px-4 py-2.5 text-slate-700 print:text-slate-800 border border-slate-200 print:border-black ${bold ? 'font-bold' : ''}`}>{label}</td>
            <td className={`px-4 py-2.5 text-right text-slate-900 print:text-black border border-slate-200 print:border-black font-mono ${bold ? 'font-bold' : ''}`}>{value}</td>
        </tr>
    );
}
